import { Fragment, useLayoutEffect, useRef, useState } from "react"
import { Button, Stack, Tooltip, useDefaultArticleItemState } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { classes } from "@tm/utils"
import { styled } from "@mui/material"
import { ContentText } from "."
import { Divider } from "../../../muiStyled/DataDisplay/divider"

const SpacedDivider = styled(Divider)({
    margin: "0 10px",
})

type Props = {
    contents: string[]
    handleClickDetails: (subPage?: string | undefined, scrollTo?: string | undefined) => void
}

const MAX_INFORMATION_LINES = 2

export function GeneralInformationContent(props: Props) {
    const { contents, handleClickDetails } = props
    const { translateText } = useLocalization()
    const tileView = useDefaultArticleItemState((x) => x.options.tileView)

    const generalInfoStack = useRef<HTMLDivElement>(null)
    const [hasMoreButton, setHasMoreButton] = useState(false)
    const [lastItem, setLastItem] = useState(contents.length)
    const [lastLineClamp, setLastLineClamp] = useState(MAX_INFORMATION_LINES)

    useLayoutEffect(() => {
        if (!tileView || !generalInfoStack.current) {
            return
        }

        // measure immediately (prevents initial toggling)
        checkContent()

        // observe for later resizes (it is required due to grid resizing e.g. when toggling list filters)
        const observer = new ResizeObserver(checkContent)
        observer.observe(generalInfoStack.current)

        return () => observer.disconnect()
    }, [tileView])

    function checkContent() {
        if (!generalInfoStack?.current?.children?.length) {
            return
        }

        // filter out everything that is not a paragraph (dividers or the "more" button)
        const paragraphs = Array.from(generalInfoStack?.current.children).filter((el) => el.nodeName === "P")

        let usedLines = 0
        let lastIndex = contents.length
        let remainingLines = MAX_INFORMATION_LINES

        const isMoreButton = paragraphs.some((item, index) => {
            // use Math.floor to prevent rounding issues when browser zoom is used
            const itemHeight = Math.floor(item.scrollHeight)
            const style = window.getComputedStyle(item)
            const lineHeight = Math.floor(parseFloat(style.lineHeight))

            // get number of lines for current paragraph
            const numberOfLines = Math.floor(itemHeight / lineHeight)
            remainingLines = MAX_INFORMATION_LINES - usedLines

            // break loop (and toggle a cutoff and the more button) when remaining lines are exceeded or when reached exactly (unless it was the last item)
            if (
                usedLines + numberOfLines > MAX_INFORMATION_LINES ||
                // compare with inital contents.length to prevent inifinite toggling
                (usedLines + numberOfLines === MAX_INFORMATION_LINES && index < contents.length - 1)
            ) {
                lastIndex = index + 1
                return true
            }

            usedLines += numberOfLines
            return false
        })

        setHasMoreButton((prev) => (prev !== isMoreButton ? isMoreButton : prev))
        setLastItem((prev) => (prev !== lastIndex ? lastIndex : prev))
        setLastLineClamp((prev) => (prev !== remainingLines ? remainingLines : prev))
    }

    return (
        <Stack
            ref={generalInfoStack}
            ml={tileView ? 2 : 3}
            flexDirection={tileView ? "column" : "row"}
            rowGap={0.5}
            alignItems={tileView ? "start" : "normal"}
        >
            {(tileView ? contents.slice(0, lastItem) : contents).map((content, index) => (
                <Fragment key={`informationEntry_${content}`}>
                    <Tooltip title={content}>
                        <ContentText
                            variant="body2"
                            className={classes(tileView && "tileView")}
                            sx={tileView ? { WebkitLineClamp: index === lastItem - 1 ? lastLineClamp : MAX_INFORMATION_LINES } : {}}
                        >
                            {content}
                        </ContentText>
                    </Tooltip>
                    {index < lastItem - 1 && <SpacedDivider light orientation="vertical" flexItem />}
                </Fragment>
            ))}
            {hasMoreButton && (
                <Button
                    variant="text"
                    color="primary"
                    style={{ padding: "0 10px 0 0", fontSize: 12, alignSelf: "end" }}
                    onClick={() => handleClickDetails()}
                >
                    {translateText(44)}
                </Button>
            )}
        </Stack>
    )
}
